import { useGetTaxonomyLabelsQuery } from '@modules/taxonomy/hooks';
import dayjs from 'dayjs';
import {
  LabelType,
  TaxonomySortField,
  TaxonomySortOrder,
} from 'generated/graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FeedbackLabelType } from 'v2/core/entities/feedback';
import { ReasonOrEntityLabelType } from 'v2/core/entities/feedback/labels';
import { useGetUser } from 'v2/core/services/user';

export const useFetchTrackedKeywords = () => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const [trackedKeywords, setTrackedKeywords] = useState<
    ReasonOrEntityLabelType[]
  >([]);
  const pageNumber = useRef(0);
  const searchFilterInput = useRef<string>();
  const hits = useRef(0);
  const nextPageNumber = useRef<number>();

  const [fetchTaxonomyLabelsQuery, { loading }] = useGetTaxonomyLabelsQuery({
    onCompleted: (data) => {
      if (!data.getTaxonomy) {
        return;
      }
      hits.current = data.getTaxonomy.hits;
      nextPageNumber.current = data.getTaxonomy.nextPage;
      const trackedKeywordsLabels = data.getTaxonomy.nodes.map(
        (node) => node.label,
      );

      const newTrackedKeywords = trackedKeywordsLabels.map(
        (trackedKeywordsLabel) => {
          const mappedReason: ReasonOrEntityLabelType = {
            ...trackedKeywordsLabel,
            createdAt: dayjs(trackedKeywordsLabel.createdAt),
            type: FeedbackLabelType.Entity,
          };
          return mappedReason;
        },
      );

      setTrackedKeywords((prevTrackedKeywords) => [
        ...prevTrackedKeywords,
        ...newTrackedKeywords,
      ]);
    },
  });

  const fetchTrackedKeywords = useCallback(
    async ({ page, searchFilter }: { page: number; searchFilter?: string }) => {
      if (
        page === pageNumber.current &&
        searchFilter === searchFilterInput.current
      ) {
        return;
      }

      pageNumber.current = page;
      searchFilterInput.current = searchFilter || undefined;

      if (page === 1) {
        setTrackedKeywords([]);
      }

      const resp = await fetchTaxonomyLabelsQuery({
        variables: {
          tenantID,
          labelType: LabelType.Aspect,
          page,
          responseSize: 50,
          sortField: TaxonomySortField.FeedbackVolume,
          sortOrder: TaxonomySortOrder.Desc,
          searchFilter: searchFilter || undefined,
        },
      });
      return resp;
    },
    [fetchTaxonomyLabelsQuery, tenantID],
  );

  useEffect(() => {
    if (!trackedKeywords.length && pageNumber.current === 0) {
      fetchTrackedKeywords({ page: 1 });
    }
  }, [fetchTrackedKeywords, pageNumber, trackedKeywords.length]);

  return [
    fetchTrackedKeywords,
    {
      trackedKeywords,
      pageNumber: pageNumber.current,
      nextPageNumber: nextPageNumber.current,
      hits: hits.current,
      loading,
    },
  ] as const;
};
